import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  navigationItems,
  NAVIGATION_SECTIONS,
} from "@/lib/constants/navigation";
import { SidebarNavItem } from "./SidebarNavItem";

const Sidebar = observer(({ collapsed, onCollapseChange, className = "" }) => {
  const { t } = useTran();
  // Group navigation items by section
  const groupedSections = Object.values(NAVIGATION_SECTIONS)
    .map((section) => ({
      title: section,
      items: navigationItems.filter((item) => item.section === section),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <aside
      className={cn(
        "flex h-screen flex-col overflow-visible text-sm border-r transition-[width] duration-200",
        "bg-card text-card-foreground border-border",
        collapsed ? "w-[64px]" : "w-full max-w-[300px]",
        className,
      )}
    >
      {/* Header with Brand and Collapse Toggle */}
      <div className="relative border-b border-border h-[57px] px-4 py-2">
        <div className="flex items-center h-full">
          {!collapsed && (
            <Link to="/" className="flex items-center">
              <img
                src="/logos/logo.svg"
                alt="NxStart"
                className="h-12 w-auto"
              />
            </Link>
          )}
        </div>

        {/* Collapse Toggle Button */}
        <button
          type="button"
          onClick={() => onCollapseChange?.(!collapsed)}
          className="absolute top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm hover:bg-muted hover:border-border transition-colors z-[50]"
          style={{ right: "-12px" }}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-current" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-current" />
          )}
        </button>
      </div>

      {/* Navigation */}
      {collapsed ? (
        <nav className="flex flex-1 flex-col items-center overflow-y-auto px-2 py-5 w-[64px]">
          <div className="flex flex-col items-center gap-2">
            {navigationItems.map((item) => (
              <SidebarNavItem
                key={item.label}
                item={item}
                collapsed={collapsed}
              />
            ))}
          </div>
        </nav>
      ) : (
        <nav className="flex-1 overflow-y-auto px-4 py-6">
          <div className="space-y-6">
            {groupedSections.map(({ title, items }) => (
              <div key={title} className="space-y-2">
                <p className="text-[11px] font-semibold uppercase text-muted-foreground">
                  {t(title)}
                </p>
                <div className="space-y-1">
                  {items.map((item) => (
                    <SidebarNavItem
                      key={item.label}
                      item={item}
                      collapsed={collapsed}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>
      )}
    </aside>
  );
});

export default Sidebar;
